import React, { useState, useEffect, useRef } from "react";
import "./InventoryTransferForm.css";

const StartStocktakeSessionForm = ({
  onClose,
  inventory,
  fetchInventory,
  userId,
  onSessionAdded,
}) => {
  const [selectedInventory, setSelectedInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]); // 🆕 new state
  const [locationId, setLocationId] = useState("");
  const [departmentId, setDepartmentId] = useState(""); // 🆕 new state
  const [sessionName, setSessionName] = useState("");
  const selectAllRef = useRef(null);

  // Fetch locations
  useEffect(() => {
    fetch("/api/locations")
      .then((res) => res.json())
      .then((data) => setLocations(data))
      .catch((err) => console.error("Error fetching locations:", err));
  }, []);

  // 🆕 Fetch departments
  useEffect(() => {
    fetch("/api/departments")
      .then((res) => res.json())
      .then((data) => setDepartments(data))
      .catch((err) => console.error("Error fetching departments:", err));
  }, []);

  const handleInventorySelection = (item_code) => {
    setSelectedInventory((prev) =>
      prev.includes(item_code)
        ? prev.filter((code) => code !== item_code)
        : [...prev, item_code]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!sessionName.trim()) {
      alert("Please enter a session name.");
      return;
    }
    if (selectedInventory.length === 0) {
      alert("Please select at least one inventory item.");
      return;
    }

    const payload = {
      name: sessionName,
      location_id: locationId || null,
      department_id: departmentId || null, // 🆕 include department filter if selected
      item_codes: selectedInventory,
      started_by_user_id: userId,
      compute_expected: true,
    };

    try {
      const res = await fetch("/api/stocktake/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        alert(`Stocktake Session Created (ID: ${data.session_id})`);
        onSessionAdded?.();
        fetchInventory?.();
        onClose();
      } else {
        alert(data.error || "Failed to create stocktake session");
      }
    } catch (error) {
      console.error("Error creating stocktake session:", error);
      alert("Server error, please try again.");
    }
  };

  // 🧠 Updated filtering logic
  const filteredInventory = inventory.filter((item) => {
    const matchesLocation =
      !locationId || String(item.location_id) === String(locationId);

    const matchesDepartment =
      !departmentId || String(item.department_id) === String(departmentId);

    const matchesSearch =
      [item.item_first_name, item.item_second_name, item.item_code, item.serial_number]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesLocation && matchesDepartment && matchesSearch;
  });

  const visibleCodes = filteredInventory.map((i) => i.item_code);
  const allVisibleSelected =
    visibleCodes.length > 0 &&
    visibleCodes.every((c) => selectedInventory.includes(c));
  const someVisibleSelected =
    visibleCodes.some((c) => selectedInventory.includes(c)) &&
    !allVisibleSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedInventory((prev) =>
        prev.filter((code) => !visibleCodes.includes(code))
      );
    } else {
      setSelectedInventory((prev) =>
        Array.from(new Set([...prev, ...visibleCodes]))
      );
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="close-button" onClick={onClose}>
          ✖
        </button>

        <form onSubmit={handleSubmit}>
          <h3>Start New Stocktake Session</h3>

          <div className="form-group">
            <label id="add-user-label">Session Name:</label>
            <input
              id="add-user-input"
              type="text"
              placeholder="Enter session name"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label id="add-user-label">Filter by Location:</label>
            <select
              id="add-user-input"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.location_id} value={loc.location_id}>
                  {loc.location_name}
                </option>
              ))}
            </select>
          </div>

          {/* 🆕 Filter by Department */}
          <div className="form-group">
            <label id="add-user-label">Filter by Department:</label>
            <select
              id="add-user-input"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
            >
              <option value="">All Departments</option>
              {departments.map((dept) => (
                <option key={dept.department_id} value={dept.department_id}>
                  {dept.department_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label id="add-user-label">Search Inventory Items:</label>
            <input
              id="add-user-input"
              type="text"
              placeholder="Search by name, code, or serial number"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="inventory-selection-container">
            <table>
              <thead>
                <tr>
                  <th className="first-child">
                    <input
                      type="checkbox"
                      ref={selectAllRef}
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all visible"
                    />
                  </th>
                  <th>First Name</th>
                  <th>Second Name</th>
                  <th>Product Code</th>
                  <th>Serial Number</th>
                  <th>Department</th> {/* 🆕 show department name */}
                </tr>
              </thead>
              <tbody>
                {filteredInventory.length > 0 ? (
                  filteredInventory.map((item) => (
                    <tr key={item.item_code}>
                      <td className="first-child">
                        <input
                          type="checkbox"
                          value={item.item_code}
                          checked={selectedInventory.includes(item.item_code)}
                          onChange={() =>
                            handleInventorySelection(item.item_code)
                          }
                        />
                      </td>
                      <td>{item.item_first_name}</td>
                      <td>{item.item_second_name}</td>
                      <td>{item.item_code}</td>
                      <td>{item.serial_number}</td>
                      <td>{item.department_name}</td> {/* 🆕 display */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">No inventory items match your search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <button type="submit" className="submit-btn">
            Start Stocktake
          </button>
        </form>
      </div>
    </div>
  );
};

export default StartStocktakeSessionForm;
